import {
  Badge,
  Group,
  Progress,
  rem,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  formatLimitUsage,
  formatLimitValue,
  getProgressColor,
  isResourceScopedLimit,
  LimitInfo,
} from "../../../business/limits.hook";

const useStyles = createStyles((theme) => ({
  bar: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: `${rem(10)} ${rem(14)}`,
  },
  barLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    color: theme.colors.dark[1],
  },
  barValue: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    color: theme.colors.dark[2],
  },
}));

export interface LimitUsageBarProps {
  limit: LimitInfo;
  compact?: boolean;
  label?: string;
}

export const LimitUsageBar = ({
  limit,
  compact = false,
  label,
}: LimitUsageBarProps) => {
  const { classes } = useStyles();
  const displayLabel = label ?? limit.name;
  const maxStr = formatLimitValue(limit.max, limit.type);

  if (isResourceScopedLimit(limit)) {
    const row = (
      <Group justify="space-between">
        <Text className={classes.barLabel} size={compact ? "xs" : undefined}>
          {displayLabel}
        </Text>
        <Text className={classes.barValue}>{maxStr}</Text>
      </Group>
    );

    return compact ? (
      <Tooltip label={`${displayLabel}: ${maxStr} per resource`}>{row}</Tooltip>
    ) : (
      <div className={classes.bar}>{row}</div>
    );
  }

  const color = getProgressColor(limit.pct);
  const usageStr = formatLimitUsage(limit);

  if (compact) {
    return (
      <Tooltip label={`${displayLabel}: ${usageStr}`}>
        <Stack gap={4}>
          <Group justify="space-between">
            <Text className={classes.barLabel} size="xs">
              {displayLabel}
            </Text>
            <Text className={classes.barValue}>{usageStr}</Text>
          </Group>
          <Progress value={limit.pct} color={color} size="xs" radius="xl" />
        </Stack>
      </Tooltip>
    );
  }

  return (
    <div className={classes.bar}>
      <Stack gap={6}>
        <Group justify="space-between">
          <Text className={classes.barLabel}>{displayLabel}</Text>
          <Text className={classes.barValue}>{usageStr}</Text>
        </Group>
        <Progress value={limit.pct} color={color} size="sm" radius="xl" />
      </Stack>
    </div>
  );
};

export interface LimitBadgeProps {
  limit: LimitInfo;
  label?: string;
}

export const LimitBadge = ({ limit, label }: LimitBadgeProps) => {
  const maxStr = formatLimitValue(limit.max, limit.type);

  if (isResourceScopedLimit(limit)) {
    return (
      <Tooltip label={label ?? limit.name}>
        <Badge variant="light" color="gray" size="sm">
          {maxStr}
        </Badge>
      </Tooltip>
    );
  }

  const color = getProgressColor(limit.pct);

  return (
    <Tooltip label={label ?? limit.name}>
      <Badge variant="light" color={color} size="sm">
        {formatLimitUsage(limit)}
      </Badge>
    </Tooltip>
  );
};
